import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { employeeAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

function slugify(text) {
  return (text || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .replace(/[đĐ]/g, m => m === 'đ' ? 'd' : 'D')
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .trim()
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-');
}

const STATUS_COLORS = {
  active:     { bg: '#dcfce7', color: '#16a34a', label: 'Đang làm' },
  probation:  { bg: '#fef9c3', color: '#ca8a04', label: 'Thử việc' },
  inactive:   { bg: '#f1f5f9', color: '#64748b', label: 'Tạm ngừng' },
  resigned:   { bg: '#fee2e2', color: '#dc2626', label: 'Đã nghỉ' },
  terminated: { bg: '#fce7f3', color: '#9d174d', label: 'Đã sa thải' },
};

const STATUS_OPTIONS = [
  { value: 'active',     label: 'Đang làm việc',  desc: 'Nhân viên đang làm việc bình thường',    icon: '✅', color: '#16a34a' },
  { value: 'probation',  label: 'Thử việc',        desc: 'Đang trong giai đoạn thử việc',           icon: '🔄', color: '#ca8a04' },
  { value: 'inactive',   label: 'Tạm ngừng',       desc: 'Tạm thời ngừng hoạt động (thai sản…)',   icon: '⏸️', color: '#64748b' },
  { value: 'resigned',   label: 'Đã nghỉ việc',    desc: 'Nhân viên đã nghỉ theo nguyện vọng',     icon: '🚶', color: '#dc2626' },
  { value: 'terminated', label: 'Đã sa thải',      desc: 'Chấm dứt hợp đồng lao động',             icon: '❌', color: '#9d174d' },
];

const CONTRACT_LABELS = {
  permanent: 'Không thời hạn', fixed: 'Có thời hạn',
  probation: 'Thử việc', parttime: 'Bán thời gian',
};

const fmt = n => n ? new Intl.NumberFormat('vi-VN').format(n) : '—';

export default function EmployeesPage() {
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deptFilter, setDeptFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active');
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ first_name: '', last_name: '', email: '', phone: '', join_date: '', department_id: '', position_id: '', contract_type: 'permanent', gender: 'male', base_salary: '' });
  const [statusModal, setStatusModal] = useState(null); // { emp, newStatus, reason, saving }
  const [deleteModal, setDeleteModal] = useState(null); // { emp, confirm, saving }
  const navigate = useNavigate();
  const { user } = useAuth();

  const load = () => {
    setLoading(true);
    Promise.all([
      employeeAPI.list({ search, department_id: deptFilter, status: statusFilter }),
      employeeAPI.departments(),
    ]).then(([empRes, deptRes]) => {
      setEmployees(empRes.data.data);
      setDepartments(deptRes.data.data);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [search, deptFilter, statusFilter]);

  const canEdit       = ['admin', 'hr_manager', 'hr_staff'].includes(user?.role);
  const canChangeStatus = ['admin', 'hr_manager'].includes(user?.role);

  const handleDelete = async () => {
    if (!deleteModal) return;
    setDeleteModal(m => ({ ...m, saving: true }));
    try {
      await employeeAPI.delete(deleteModal.emp.id);
      setDeleteModal(null);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Lỗi xóa nhân viên');
      setDeleteModal(m => ({ ...m, saving: false }));
    }
  };

  const handleStatusSave = async () => {
    if (!statusModal) return;
    setStatusModal(m => ({ ...m, saving: true }));
    try {
      await employeeAPI.update(statusModal.emp.id, { status: statusModal.newStatus });
      setStatusModal(null);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Lỗi cập nhật trạng thái');
      setStatusModal(m => ({ ...m, saving: false }));
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    try {
      await employeeAPI.create(form);
      setShowForm(false);
      load();
    } catch (err) {
      alert(err.response?.data?.message || 'Lỗi tạo nhân viên');
    }
  };

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#1e293b', margin: 0 }}>👥 Danh sách nhân viên</h1>
        {canEdit && (
          <button onClick={() => setShowForm(true)} style={{
            padding: '10px 20px', background: 'linear-gradient(135deg,#1e3a8a,#0891b2)',
            color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 14
          }}>
            + Thêm nhân viên
          </button>
        )}
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <input
          placeholder="🔍 Tìm tên, mã, email..."
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ padding: '8px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14, width: 220 }}
        />
        <select value={deptFilter} onChange={e => setDeptFilter(e.target.value)}
          style={{ padding: '8px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14 }}>
          <option value="">Tất cả phòng ban</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </select>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          style={{ padding: '8px 14px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 14 }}>
          <option value="">Tất cả trạng thái</option>
          <option value="active">Đang làm</option>
          <option value="probation">Thử việc</option>
          <option value="inactive">Tạm ngừng</option>
          <option value="resigned">Đã nghỉ</option>
          <option value="terminated">Đã sa thải</option>
        </select>
        <span style={{ padding: '8px 14px', background: '#f1f5f9', borderRadius: 8, fontSize: 14, color: '#64748b' }}>
          {employees.length} nhân viên
        </span>
      </div>

      {/* Table */}
      <div style={{ background: '#fff', borderRadius: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
          <thead>
            <tr style={{ background: '#f8fafc', borderBottom: '2px solid #e2e8f0' }}>
              {['Mã NV', 'Họ tên', 'Phòng ban', 'Chức vụ', 'Loại HĐ', 'Lương cơ bản', 'Trạng thái', 'Hành động'].map(h => (
                <th key={h} style={{ padding: '12px 14px', textAlign: 'left', fontWeight: 600, color: '#374151', fontSize: 12, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>⏳ Đang tải...</td></tr>
            ) : employees.map(emp => {
              const st = STATUS_COLORS[emp.status] || STATUS_COLORS.active;
              return (
                <tr key={emp.id} style={{ borderBottom: '1px solid #f8fafc', cursor: 'pointer' }}
                  onClick={() => navigate(`/employees/${emp.id}/${slugify(emp.first_name + ' ' + emp.last_name)}`)}
                  onMouseEnter={e => e.currentTarget.style.background = '#f0f9ff'}
                  onMouseLeave={e => e.currentTarget.style.background = ''}>
                  <td style={{ padding: '12px 14px' }}>
                    <span style={{ fontFamily: 'monospace', fontWeight: 600, color: '#1e3a8a', fontSize: 12 }}>{emp.employee_code}</span>
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div style={{
                        width: 34, height: 34, borderRadius: '50%',
                        background: `hsl(${emp.id * 50}, 60%, 50%)`,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        color: '#fff', fontWeight: 700, fontSize: 12, flexShrink: 0
                      }}>
                        {emp.first_name.charAt(0)}
                      </div>
                      <div>
                        <div style={{ fontWeight: 600, color: '#1d4ed8' }}>{emp.first_name} {emp.last_name}</div>
                        <div style={{ fontSize: 11, color: '#94a3b8' }}>{emp.email} · Nhấn để xem chi tiết →</div>
                      </div>
                    </div>
                  </td>
                  <td style={{ padding: '12px 14px', color: '#64748b' }}>{emp.department_name}</td>
                  <td style={{ padding: '12px 14px', color: '#64748b' }}>{emp.position_title}</td>
                  <td style={{ padding: '12px 14px' }}>
                    <span style={{ fontSize: 11, color: '#64748b' }}>{CONTRACT_LABELS[emp.contract_type]}</span>
                  </td>
                  <td style={{ padding: '12px 14px', fontWeight: 600, color: '#1e293b' }}>
                    {fmt(emp.base_salary)} ₫
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    <span
                      onClick={canChangeStatus ? (e) => { e.stopPropagation(); setStatusModal({ emp, newStatus: emp.status, reason: '', saving: false }); } : undefined}
                      style={{
                        background: st.bg, color: st.color, padding: '3px 10px', borderRadius: 12,
                        fontSize: 11, fontWeight: 600,
                        cursor: canChangeStatus ? 'pointer' : 'default',
                        border: canChangeStatus ? `1px solid ${st.color}40` : 'none',
                        display: 'inline-flex', alignItems: 'center', gap: 4,
                      }}
                      title={canChangeStatus ? 'Nhấn để đổi trạng thái' : undefined}
                    >
                      {st.label}
                      {canChangeStatus && <span style={{ fontSize: 9, opacity: 0.7 }}>✏️</span>}
                    </span>
                  </td>
                  <td style={{ padding: '12px 14px' }}>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <button onClick={(e) => { e.stopPropagation(); navigate(`/employees/${emp.id}/${slugify(emp.first_name + ' ' + emp.last_name)}`); }}
                        style={{ padding: '5px 12px', background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}>
                        Chi tiết
                      </button>
                      {canChangeStatus && (
                        <button onClick={(e) => { e.stopPropagation(); setStatusModal({ emp, newStatus: emp.status, reason: '', saving: false }); }}
                          style={{ padding: '5px 10px', background: '#f8fafc', color: '#64748b', border: '1px solid #e2e8f0', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
                          title="Đổi trạng thái">
                          ⚙️
                        </button>
                      )}
                      {user?.role === 'admin' && (
                        <button onClick={(e) => { e.stopPropagation(); setDeleteModal({ emp, confirm: '', saving: false }); }}
                          style={{ padding: '5px 10px', background: '#fff0f0', color: '#dc2626', border: '1px solid #fecaca', borderRadius: 6, cursor: 'pointer', fontSize: 12 }}
                          title="Xóa nhân viên">
                          🗑️
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Delete confirmation modal */}
      {deleteModal && (() => {
        const fullName = `${deleteModal.emp.first_name} ${deleteModal.emp.last_name}`;
        const confirmed = deleteModal.confirm.trim() === fullName;
        return (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 440, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
              <div style={{ textAlign: 'center', marginBottom: 20 }}>
                <div style={{ fontSize: 44, marginBottom: 8 }}>⚠️</div>
                <h3 style={{ margin: '0 0 6px', fontSize: 17, fontWeight: 700, color: '#1e293b' }}>Xóa nhân viên?</h3>
                <p style={{ margin: 0, fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
                  Hành động này sẽ xóa vĩnh viễn <strong style={{ color: '#1e293b' }}>{fullName}</strong> và toàn bộ dữ liệu liên quan
                  (chấm công, nghỉ phép, lương, nhiệm vụ…). Không thể hoàn tác.
                </p>
              </div>

              <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '10px 14px', marginBottom: 18, fontSize: 12, color: '#9a3412' }}>
                Nhập tên nhân viên để xác nhận: <strong>{fullName}</strong>
              </div>

              <input
                value={deleteModal.confirm}
                onChange={e => setDeleteModal(m => ({ ...m, confirm: e.target.value }))}
                placeholder={`Nhập "${fullName}"...`}
                style={{
                  width: '100%', padding: '9px 12px', border: `1px solid ${confirmed ? '#22c55e' : '#e2e8f0'}`,
                  borderRadius: 8, fontSize: 13, boxSizing: 'border-box',
                  outline: 'none', marginBottom: 18,
                  background: confirmed ? '#f0fdf4' : '#fff',
                }}
              />

              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
                <button onClick={() => setDeleteModal(null)}
                  style={{ padding: '9px 20px', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', background: '#fff', fontSize: 13 }}>
                  Hủy
                </button>
                <button
                  onClick={handleDelete}
                  disabled={!confirmed || deleteModal.saving}
                  style={{
                    padding: '9px 22px', borderRadius: 8, border: 'none',
                    fontSize: 13, fontWeight: 600, color: '#fff',
                    background: confirmed ? '#dc2626' : '#94a3b8',
                    cursor: confirmed ? 'pointer' : 'not-allowed',
                    opacity: deleteModal.saving ? 0.7 : 1,
                  }}>
                  {deleteModal.saving ? 'Đang xóa...' : '🗑️ Xóa vĩnh viễn'}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Status change modal */}
      {statusModal && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: 460, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            {/* Header */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
              <div style={{
                width: 42, height: 42, borderRadius: '50%', flexShrink: 0,
                background: `hsl(${statusModal.emp.id * 50}, 60%, 50%)`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontWeight: 700, fontSize: 16,
              }}>
                {statusModal.emp.first_name?.charAt(0)}
              </div>
              <div>
                <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b' }}>
                  {statusModal.emp.first_name} {statusModal.emp.last_name}
                </div>
                <div style={{ fontSize: 12, color: '#64748b' }}>
                  {statusModal.emp.employee_code} · {statusModal.emp.department_name}
                </div>
              </div>
              <button onClick={() => setStatusModal(null)}
                style={{ marginLeft: 'auto', background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8', lineHeight: 1 }}>×</button>
            </div>

            <h4 style={{ margin: '0 0 14px', fontSize: 14, fontWeight: 700, color: '#374151' }}>Chọn trạng thái mới</h4>

            {/* Status radio cards */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 18 }}>
              {STATUS_OPTIONS.map(opt => {
                const selected = statusModal.newStatus === opt.value;
                const isCurrent = statusModal.emp.status === opt.value;
                return (
                  <label key={opt.value} style={{
                    display: 'flex', alignItems: 'center', gap: 12,
                    padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
                    border: `2px solid ${selected ? opt.color : '#f1f5f9'}`,
                    background: selected ? `${opt.color}0d` : '#fafafa',
                    transition: 'all 0.12s',
                  }}>
                    <input type="radio" name="status" value={opt.value}
                      checked={selected}
                      onChange={() => setStatusModal(m => ({ ...m, newStatus: opt.value }))}
                      style={{ accentColor: opt.color, width: 16, height: 16, flexShrink: 0 }} />
                    <span style={{ fontSize: 18, flexShrink: 0 }}>{opt.icon}</span>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 600, color: selected ? opt.color : '#374151', display: 'flex', alignItems: 'center', gap: 6 }}>
                        {opt.label}
                        {isCurrent && <span style={{ fontSize: 10, background: '#f1f5f9', color: '#64748b', padding: '1px 6px', borderRadius: 8 }}>Hiện tại</span>}
                      </div>
                      <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 1 }}>{opt.desc}</div>
                    </div>
                  </label>
                );
              })}
            </div>

            {/* Buttons */}
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button onClick={() => setStatusModal(null)}
                style={{ padding: '9px 20px', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', background: '#fff', fontSize: 13 }}>
                Hủy
              </button>
              <button
                onClick={handleStatusSave}
                disabled={statusModal.saving || statusModal.newStatus === statusModal.emp.status}
                style={{
                  padding: '9px 22px', borderRadius: 8, border: 'none', cursor: 'pointer',
                  fontSize: 13, fontWeight: 600, color: '#fff',
                  background: statusModal.newStatus === statusModal.emp.status ? '#94a3b8' : 'linear-gradient(135deg,#1e3a8a,#0891b2)',
                  opacity: statusModal.saving ? 0.7 : 1,
                }}>
                {statusModal.saving ? 'Đang lưu...' : 'Xác nhận'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create modal */}
      {showForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 100, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 32, width: 560, maxHeight: '90vh', overflow: 'auto' }}>
            <h3 style={{ margin: '0 0 24px', fontSize: 18, fontWeight: 700 }}>Thêm nhân viên mới</h3>
            <form onSubmit={handleCreate}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
                {[
                  { key: 'first_name', label: 'Tên', req: true },
                  { key: 'last_name', label: 'Họ', req: true },
                  { key: 'email', label: 'Email', type: 'email', req: true },
                  { key: 'phone', label: 'Điện thoại' },
                  { key: 'join_date', label: 'Ngày vào làm', type: 'date', req: true },
                  { key: 'base_salary', label: 'Lương cơ bản', type: 'number' },
                ].map(f => (
                  <div key={f.key}>
                    <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>{f.label}</label>
                    <input type={f.type || 'text'} required={f.req} value={form[f.key]} onChange={e => setForm({...form, [f.key]: e.target.value})}
                      style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' }} />
                  </div>
                ))}
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Phòng ban</label>
                  <select value={form.department_id} onChange={e => setForm({...form, department_id: e.target.value})}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
                    <option value="">Chọn phòng ban</option>
                    {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <div>
                  <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>Loại HĐ</label>
                  <select value={form.contract_type} onChange={e => setForm({...form, contract_type: e.target.value})}
                    style={{ width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 13 }}>
                    <option value="permanent">Không thời hạn</option>
                    <option value="fixed">Có thời hạn</option>
                    <option value="probation">Thử việc</option>
                  </select>
                </div>
              </div>
              <div style={{ display: 'flex', gap: 12, marginTop: 24, justifyContent: 'flex-end' }}>
                <button type="button" onClick={() => setShowForm(false)}
                  style={{ padding: '10px 20px', border: '1px solid #e2e8f0', borderRadius: 8, cursor: 'pointer', background: '#fff' }}>
                  Hủy
                </button>
                <button type="submit" style={{ padding: '10px 24px', background: '#1e3a8a', color: '#fff', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600 }}>
                  Tạo nhân viên
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
